import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Divider,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  Chip,
  IconButton,
  Checkbox,
  InputAdornment,
  Collapse,
  ListItemText,
} from '@mui/material';
import { PersonAddOutlined, EditOutlined, CheckCircle, Cancel, Add, Close, ExpandMore, ExpandLess, Visibility, VisibilityOff, LockReset } from '@mui/icons-material';
import { api } from '../../services/api';
import { ManagedUser, UserRole } from '../../types/api';
import { INDIA_STATES_WITH_IDS, getStateNameById } from '../../constants/indiaLocations';
import {
  PagePermissions,
  PAGE_PERMISSION_LABELS,
  DEFAULT_PAGE_PERMISSIONS,
  PagePermissionKey,
  PERMISSION_SECTIONS,
  PermissionSection,
  toNestedPermissions,
  normalizePermissions,
} from '../../constants/pages';

interface UserFormProps {
  mode: 'create' | 'edit';
}

const UserForm: React.FC<UserFormProps> = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [role, setRole] = useState<UserRole>('SALES_TEAM');
  const [permissions, setPermissions] = useState<PagePermissions>({ ...DEFAULT_PAGE_PERMISSIONS });
  const [allowedIps, setAllowedIps] = useState<string[]>([]);
  const [newIp, setNewIp] = useState('');
  const [stateIds, setStateIds] = useState<number[]>([]);
  const [hasWorkingHours, setHasWorkingHours] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [workingHoursStart, setWorkingHoursStart] = useState('09:00');
  const [workingHoursEnd, setWorkingHoursEnd] = useState('18:00');
  const [isEnabled, setIsEnabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch user details in edit mode
  useEffect(() => {
    if (mode === 'edit' && id) {
      const fetchUser = async () => {
        setFetching(true);
        try {
          const response = await api.get(`/api/users/${id}`);
          const user: ManagedUser = response.data;
          setName(user.name || '');
          setEmail(user.email || '');
          setRole(user.role || 'SALES_TEAM');
          // Normalize permissions from API (handles both flat and nested formats)
          setPermissions(normalizePermissions(user.permissions));
          setAllowedIps(user.allowed_ips || []);
          setStateIds(user.state_ids || []);
          setHasWorkingHours(!!user.working_hours);
          setWorkingHoursStart(user.working_hours?.start_time || '09:00');
          setWorkingHoursEnd(user.working_hours?.end_time || '18:00');
          setIsEnabled(user.is_enabled ?? true);
        } catch (err) {
          console.error('Error fetching user:', err);
          setError('Failed to load user details');
        } finally {
          setFetching(false);
        }
      };
      fetchUser();
    }
  }, [mode, id]);

  const handlePermissionChange = (key: PagePermissionKey) => {
    setPermissions((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSectionToggle = (section: PermissionSection) => {
    const allEnabled = section.keys.every(key => permissions[key]);
    setPermissions((prev) => {
      const updated = { ...prev };
      section.keys.forEach(key => {
        updated[key] = !allEnabled;
      });
      return updated;
    });
  };

  const toggleSectionExpand = (sectionName: string) => {
    setExpandedSections(prev =>
      prev.includes(sectionName)
        ? prev.filter(s => s !== sectionName)
        : [...prev, sectionName]
    );
  };

  const getSectionCheckState = (section: PermissionSection): 'all' | 'some' | 'none' => {
    const enabledCount = section.keys.filter(key => permissions[key]).length;
    if (enabledCount === section.keys.length) return 'all';
    if (enabledCount > 0) return 'some';
    return 'none';
  };

  const handleAddIp = () => {
    const ip = newIp.trim();
    if (ip && !allowedIps.includes(ip)) {
      setAllowedIps([...allowedIps, ip]);
      setNewIp('');
    }
  };

  const handleRemoveIp = (ip: string) => {
    setAllowedIps(allowedIps.filter((i) => i !== ip));
  };

  const validateForm = (): boolean => {
    if (!name.trim()) {
      setError('Name is required');
      return false;
    }
    if (!email.trim()) {
      setError('Email is required');
      return false;
    }
    if (mode === 'create' && !password) {
      setError('Password is required');
      return false;
    }
    if (mode === 'create' && password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    if (mode === 'edit' && showPasswordSection && newPassword.trim() && newPassword.trim().length < 6) {
      setError('New password must be at least 6 characters');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) return;

    setLoading(true);

    // Convert flat permissions to nested structure for API
    const nestedPermissions = toNestedPermissions(permissions);

    const payload: any = {
      name: name.trim(),
      role,
      permissions: nestedPermissions,
      is_enabled: isEnabled,
      allowed_ips: allowedIps, // Always send, empty list if none
      state_ids: stateIds, // Always send, empty list means "all states"
    };

    if (hasWorkingHours && workingHoursStart && workingHoursEnd) {
      payload.working_hours = {
        start_time: workingHoursStart,
        end_time: workingHoursEnd,
      };
    }

    if (mode === 'create') {
      payload.email = email.trim();
      payload.password = password;
    }

    // Include new password in edit mode only if toggle is on and password is non-empty
    if (mode === 'edit' && showPasswordSection && newPassword.trim()) {
      payload.password = newPassword.trim();
    }

    try {
      if (mode === 'create') {
        await api.post('/api/users', payload);
      } else {
        await api.put(`/api/users/${id}`, payload);
      }
      navigate('/users');
    } catch (err) {
      setError('Failed to save user');
      console.error('Error saving user:', err);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 4, maxWidth: 800, mx: 'auto', mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        {mode === 'create' ? (
          <PersonAddOutlined sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
        ) : (
          <EditOutlined sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
        )}
        <Typography variant="h5" fontWeight="bold">
          {mode === 'create' ? 'Add New User' : 'Edit User'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        {/* Enable/Disable Toggle - Only in Edit Mode */}
        {mode === 'edit' && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              mb: 3,
              borderRadius: 2,
              bgcolor: isEnabled ? 'success.50' : 'grey.100',
              border: '1px solid',
              borderColor: isEnabled ? 'success.200' : 'grey.300',
              transition: 'all 0.2s ease-in-out',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {isEnabled ? (
                <CheckCircle sx={{ color: 'success.main', fontSize: 28 }} />
              ) : (
                <Cancel sx={{ color: 'text.disabled', fontSize: 28 }} />
              )}
              <Box>
                <Typography variant="subtitle1" fontWeight="600">
                  {isEnabled ? 'User Enabled' : 'User Disabled'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {isEnabled ? 'This user can access the system' : 'This user cannot access the system'}
                </Typography>
              </Box>
            </Box>
            <Switch
              checked={isEnabled}
              onChange={(e) => setIsEnabled(e.target.checked)}
              color="success"
            />
          </Box>
        )}

        {/* Basic Info */}
        <Typography variant="h6" gutterBottom>
          Basic Information
        </Typography>
        
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="Enter user's name"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Enter email address"
              disabled={mode === 'edit'}
            />
          </Grid>
          {mode === 'create' && (
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter password (min 6 characters)"
              />
            </Grid>
          )}
          <Grid size={{ xs: 12, md: mode === 'create' ? 6 : 12 }}>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={role}
                label="Role"
                onChange={(e) => setRole(e.target.value as UserRole)}
              >
                <MenuItem value="WAREHOUSE_TEAM">Warehouse Team</MenuItem>
                <MenuItem value="SALES_TEAM">Sales Team</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Permissions */}
        <Typography variant="h6" gutterBottom>
          Page Permissions
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Pages not listed here are accessible to all users by default.
        </Typography>
        
        <Box sx={{ mb: 3 }}>
          {PERMISSION_SECTIONS.map((section) => {
            const checkState = getSectionCheckState(section);
            const isExpanded = expandedSections.includes(section.name);
            const hasMultipleItems = section.keys.length > 1;

            return (
              <Box
                key={section.name}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 1,
                  overflow: 'hidden',
                }}
              >
                {/* Section Header */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1,
                    bgcolor: checkState === 'all' ? 'primary.50' : checkState === 'some' ? 'warning.50' : 'grey.50',
                    cursor: hasMultipleItems ? 'pointer' : 'default',
                  }}
                  onClick={() => hasMultipleItems && toggleSectionExpand(section.name)}
                >
                  <FormControlLabel
                    onClick={(e) => e.stopPropagation()}
                    control={
                      <Checkbox
                        checked={checkState === 'all'}
                        indeterminate={checkState === 'some'}
                        onChange={() => handleSectionToggle(section)}
                        color="primary"
                      />
                    }
                    label={
                      <Typography fontWeight="medium">
                        {section.name}
                        {hasMultipleItems && (
                          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                            ({section.keys.filter(k => permissions[k]).length}/{section.keys.length})
                          </Typography>
                        )}
                      </Typography>
                    }
                  />
                  {hasMultipleItems && (
                    <IconButton size="small">
                      {isExpanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  )}
                </Box>

                {/* Section Children */}
                {hasMultipleItems && isExpanded && (
                  <Box sx={{ px: 2, py: 1, pl: 6, bgcolor: 'background.paper' }}>
                    <Grid container spacing={1}>
                      {section.keys.map((key) => (
                        <Grid size={{ xs: 12, sm: 6 }} key={key}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={permissions[key]}
                                onChange={() => handlePermissionChange(key)}
                                color="primary"
                                size="small"
                              />
                            }
                            label={PAGE_PERMISSION_LABELS[key]}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Working Hours */}
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={hasWorkingHours}
                onChange={(e) => setHasWorkingHours(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="h6" component="span">
                Restrict Working Hours
              </Typography>
            }
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
            {hasWorkingHours
              ? 'User can only access the system during specified hours'
              : 'User can access the system at any time'}
          </Typography>
        </Box>
        
        {hasWorkingHours && (
          <Grid container spacing={2} sx={{ mb: 3, ml: 2 }}>
            <Grid size={6}>
              <TextField
                fullWidth
                label="Start Time"
                type="time"
                value={workingHoursStart}
                onChange={(e) => setWorkingHoursStart(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                label="End Time"
                type="time"
                value={workingHoursEnd}
                onChange={(e) => setWorkingHoursEnd(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Allowed IPs */}
        <Typography variant="h6" gutterBottom>
          Allowed IP Addresses <Typography component="span" variant="body2" color="text.secondary">(Optional)</Typography>
        </Typography>
        
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              size="small"
              label="Add IP Address"
              value={newIp}
              onChange={(e) => setNewIp(e.target.value)}
              placeholder="e.g., 192.168.1.1"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddIp();
                }
              }}
            />
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={handleAddIp}
              disabled={!newIp.trim()}
            >
              Add
            </Button>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {allowedIps.map((ip) => (
              <Chip
                key={ip}
                label={ip}
                onDelete={() => handleRemoveIp(ip)}
                deleteIcon={<Close />}
                variant="outlined"
              />
            ))}
            {allowedIps.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No IP restrictions (all IPs allowed)
              </Typography>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Assigned States */}
        <Typography variant="h6" gutterBottom>
          Assigned States <Typography component="span" variant="body2" color="text.secondary">(Optional)</Typography>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Restrict this user to specific states. Leave empty to allow access to all states.
        </Typography>

        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="user-states-label">States</InputLabel>
            <Select
              multiple
              labelId="user-states-label"
              label="States"
              value={stateIds}
              onChange={(e) => {
                const value = e.target.value;
                setStateIds(
                  typeof value === 'string'
                    ? value.split(',').map(Number)
                    : (value as number[])
                );
              }}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as number[]).map((sid) => (
                    <Chip key={sid} label={getStateNameById(sid) ?? String(sid)} size="small" />
                  ))}
                </Box>
              )}
            >
              {INDIA_STATES_WITH_IDS.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  <Checkbox checked={stateIds.includes(s.id)} />
                  <ListItemText primary={s.state} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Button
              size="small"
              onClick={() => setStateIds(INDIA_STATES_WITH_IDS.map((s) => s.id))}
            >
              Select All
            </Button>
            <Button size="small" onClick={() => setStateIds([])} disabled={stateIds.length === 0}>
              Clear
            </Button>
            {stateIds.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                {stateIds.length} state{stateIds.length > 1 ? 's' : ''} selected
              </Typography>
            )}
          </Box>
        </Box>

        {/* Reset Password - Only in Edit Mode */}
        {mode === 'edit' && (
          <>
            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Reset Password <Typography component="span" variant="body2" color="text.secondary">(Optional)</Typography>
            </Typography>

            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                mb: 3,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2,
                  py: 1.5,
                  bgcolor: showPasswordSection ? 'warning.50' : 'grey.50',
                  cursor: 'pointer',
                }}
                onClick={() => setShowPasswordSection(!showPasswordSection)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <LockReset sx={{ color: showPasswordSection ? 'warning.main' : 'text.secondary' }} />
                  <Box>
                    <Typography variant="subtitle2" fontWeight="600">
                      {showPasswordSection ? 'Enter New Password' : 'Click to Reset Password'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {showPasswordSection ? 'Enter a new password for this user' : 'Expand to change user password'}
                    </Typography>
                  </Box>
                </Box>
                <IconButton size="small">
                  {showPasswordSection ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              <Collapse in={showPasswordSection}>
                <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
                  <TextField
                    fullWidth
                    label="New Password"
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 6 characters)"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            edge="end"
                            size="small"
                          >
                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    helperText="Leave empty to keep current password"
                  />
                </Box>
              </Collapse>
            </Box>
          </>
        )}

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={loading}
          >
            {loading
              ? 'Saving...'
              : mode === 'create'
              ? 'Create User'
              : 'Update User'}
          </Button>
          <Button variant="outlined" onClick={() => navigate('/users')}>
            Cancel
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default UserForm;
